<template>
    <v-container class="pa-5" fluid>
        <ConfirmDialog
            :is-open="confirmDeleteNoteDialog"
            :data="productionSiteNote"
            :action-loading="actionLoading.deleteNote"
            @on-confirm="deleteNote"
            @on-abort="closeConfirmDeleteNoteDialog"
        >
            <template #title>
                Usunięcie notki
            </template>
            <template #content>
                Czy na pewno chcesz usunąć notkę?
            </template>
            <template #confirmButton>
                Potwierdź
            </template>
        </ConfirmDialog>

        <page-title>
            {{ pageTitle }}
        </page-title>

        <v-row align="center">
            <v-col class="pb-0">
                <v-btn
                    text
                    color="primary"
                    class="pa-0"
                    :disabled="actionLoading.saveNote"
                    @click="close"
                >
                    <v-icon color="primary">
                        mdi-arrow-left
                    </v-icon>
                    Powrót do listy
                </v-btn>
            </v-col>
        </v-row>
        <v-row>
            <v-col cols="12" xl="8" offset-xl="2">
                <v-container class="pa-0">
                    <v-row>
                        <v-col cols="12">
                            <v-container class="pa-0">
                                <v-row>
                                    <v-col cols="12">
                                        <v-card>
                                            <v-toolbar flat dense color="white lighten-2">
                                                <v-toolbar-title>Dane notki</v-toolbar-title>
                                            </v-toolbar>
                                            <v-divider />
                                            <v-card-text>
                                                <v-container>
                                                    <v-row>
                                                        <v-col cols="12">
                                                            <v-text-field
                                                                v-model="productionSiteNote.title"
                                                                label="Tytuł"
                                                                outlined
                                                                dense
                                                                required
                                                                :error-messages="titleErrors"
                                                            />
                                                        </v-col>
                                                        <v-col cols="12">
                                                            <v-textarea
                                                                v-model="productionSiteNote.text"
                                                                label="Opis"
                                                                outlined
                                                                dense
                                                                auto-grow
                                                                required
                                                                :error-messages="textErrors"
                                                            />
                                                        </v-col>
                                                    </v-row>
                                                    <v-row align="center">
                                                        <v-col cols="3">
                                                            <v-switch
                                                                v-model="productionSiteNote.approved"
                                                                :disabled="actionLoading.getNote"
                                                                :label="`Widoczność: ${approveLabel()}`"
                                                                @change="changeNoteAcceptance"
                                                            />
                                                        </v-col>
                                                        <v-col cols="3">
                                                            <v-switch
                                                                v-if="!isNewNote"
                                                                v-model="productionSiteNote.adminAccepted"
                                                                :disabled="!canAccept"
                                                                color="success"
                                                                :label="`${acceptLabel()} przez administratora`"
                                                                @change="changeNoteAcceptance"
                                                            />
                                                        </v-col>
                                                        <v-col cols="6">
                                                            <v-text-field
                                                                :value="userData"
                                                                label="Autor"
                                                                outlined
                                                                dense
                                                                :readonly="!isNewNote"
                                                                :disabled="isNewNote"
                                                            />
                                                        </v-col>
                                                    </v-row>
                                                    <v-row>
                                                        <v-col cols="6">
                                                            <v-datetime-picker
                                                                ref="expireDateTimePicker"
                                                                v-model="expireDateTime"
                                                                label="Planowana data wygaśnięcia"
                                                                date-format="dd.MM.yyyy"
                                                                :date-picker-props="datetimeProps.dateProps"
                                                                :text-field-props="datetimeProps.textFieldProps"
                                                                :time-picker-props="datetimeProps.timeProps"
                                                                @input="expireDateTimeHandler"
                                                            >
                                                                <template slot="dateIcon">
                                                                    <v-icon>mdi-calendar</v-icon>
                                                                </template>
                                                                <template slot="timeIcon">
                                                                    <v-icon>mdi-clock-outline</v-icon>
                                                                </template>
                                                                <template slot="actions" slot-scope="{parent}">
                                                                    <v-btn text color="error" @click.native="parent.clearHandler">
                                                                        Wyczyść
                                                                    </v-btn>
                                                                    <v-btn text color="success" @click="parent.okHandler">
                                                                        Zapisz
                                                                    </v-btn>
                                                                </template>
                                                            </v-datetime-picker>
                                                        </v-col>
                                                        <v-col cols="6">
                                                            <v-text-field
                                                                :value="formatDate(productionSiteNote.createdAt)"
                                                                label="Data utworzenia"
                                                                outlined
                                                                dense
                                                                :readonly="!isNewNote"
                                                                :disabled="isNewNote"
                                                            />
                                                        </v-col>
                                                    </v-row>
                                                    <v-row>
                                                        <v-col cols="6">
                                                            <v-autocomplete
                                                                v-model="productionSiteNote.productionSites"
                                                                :items="allProductionSites"
                                                                :search-input.sync="search"
                                                                label="Widoczność na stanowiskach:"
                                                                placeholder="Wybierz stanowiska"
                                                                item-value="id"
                                                                item-text="name"
                                                                chips
                                                                clearable
                                                                multiple
                                                                outlined
                                                                dense
                                                                hide-details
                                                                required
                                                                :error-messages="productionSitesErrors"
                                                                @change="search = null"
                                                            >
                                                                <template #selection="data">
                                                                    <v-chip
                                                                        close
                                                                        @update:active="removeProductionSite(data.item)"
                                                                    >
                                                                        {{ data.item.name }}
                                                                    </v-chip>
                                                                </template>
                                                            </v-autocomplete>
                                                        </v-col>
                                                        <v-col cols="6">
                                                            <v-text-field
                                                                :value="originProductionSite"
                                                                label="Stanowisko utworzenia"
                                                                outlined
                                                                dense
                                                                :readonly="!isNewNote"
                                                                :disabled="isNewNote"
                                                            />
                                                        </v-col>
                                                    </v-row>
                                                </v-container>
                                            </v-card-text>
                                        
                                            <v-divider v-if="canUpdate || canDelete" />

                                            <v-card-actions>
                                                <v-btn
                                                    v-if="!isNewNote && canDelete"
                                                    color="error"
                                                    text
                                                    data-sentry-label="Usuń"
                                                    :loading="actionLoading.deleteNote"
                                                    @click="openConfirmDeleteNoteDialog(productionSiteNote)"
                                                >
                                                    Usuń
                                                </v-btn>
                                                <v-spacer />
                                                <v-btn
                                                    v-if="canUpdate"
                                                    color="success"
                                                    text
                                                    data-sentry-label="Zapisz"
                                                    :loading="actionLoading.saveNote"
                                                    @click="saveNote()"
                                                >
                                                    Zapisz
                                                </v-btn>
                                            </v-card-actions>
                                        </v-card>

                                        <v-card v-if="!isNewNote" class="mt-8">
                                            <v-toolbar flat dense color="white lighten-2">
                                                <v-toolbar-title>Zapoznali się</v-toolbar-title>
                                            </v-toolbar>
                                            <v-divider />
                                            <v-card-text>
                                                <v-container>
                                                    <v-row>
                                                        <v-col cols="12">
                                                            <v-data-table
                                                                :headers="readByHeaders"
                                                                :items="productionSiteNote.productionSiteNoteReadByHistory"
                                                                :mobile-breakpoint="0"
                                                                :footer-props="footerPropsReadBy"
                                                            >
                                                                <template #item.readBy="{item}">
                                                                    <template v-if="item.readBy && item.readBy.firstName && item.readBy.lastName">
                                                                        {{ item.readBy.firstName }} {{ item.readBy.lastName }}
                                                                    </template>
                                                                    <template v-else>
                                                                        -
                                                                    </template>
                                                                </template>
                                                                <template #item.readDateTime="{item}">
                                                                    {{ formatDate(item.readDateTime) }}
                                                                </template>
                                                            </v-data-table>
                                                        </v-col>
                                                    </v-row>
                                                </v-container>
                                            </v-card-text>
                                        </v-card>
                                        
                                        <v-card v-if="!isNewNote" class="mt-8">
                                            <v-toolbar flat dense color="white lighten-2">
                                                <v-toolbar-title>Komentarze</v-toolbar-title>
                                            </v-toolbar>
                                            <v-divider />
                                            <v-card-text>
                                                <v-container>
                                                    <v-row>
                                                        <v-col cols="12">
                                                            <v-textarea
                                                                v-model="noteComment.comment"
                                                                label="Dodaj komentarz"
                                                                :disabled="!canUserCommentNote"
                                                                auto-grow
                                                                clearable
                                                                hide-details
                                                            />
                                                            <v-row justify="end">
                                                                <v-col>
                                                                    <v-btn
                                                                        color="primary"
                                                                        data-sentry-label="Wyślij komentarz"
                                                                        :disabled="noteComment === null || noteComment.comment === '' || !canUserCommentNote"
                                                                        :loading="actionLoading.addComment"
                                                                        @click="addComment"
                                                                    >
                                                                        Wyślij
                                                                    </v-btn>
                                                                </v-col>
                                                            </v-row>
                                                        </v-col>
                                                    </v-row>
                                                    <v-row>
                                                        <v-col cols="12">
                                                            <v-data-table
                                                                :headers="commentsHeaders"
                                                                :items="productionSiteNote.productionSiteNoteComments"
                                                                :loading="actionLoading.getComments"
                                                                :mobile-breakpoint="0"
                                                                :footer-props="footerPropsReadBy"
                                                            >
                                                                <template #item.createdBy="{item}">
                                                                    <template v-if="item.createdBy && item.createdBy.firstName && item.createdBy.lastName">
                                                                        {{ item.createdBy.firstName }} {{ item.createdBy.lastName }}
                                                                    </template>
                                                                    <template v-else>
                                                                        -
                                                                    </template>
                                                                </template>
                                                                <template #item.createdAt="{item}">
                                                                    {{ formatDate(item.createdAt) }}
                                                                </template>
                                                                <template #item.comment="{item}">
                                                                    {{ item.comment }}
                                                                </template>
                                                            </v-data-table>
                                                        </v-col>
                                                    </v-row>
                                                </v-container>
                                            </v-card-text>
                                        </v-card>
                                    </v-col>
                                </v-row>
                            </v-container>
                        </v-col>
                    </v-row>
                </v-container>
            </v-col>
        </v-row>
    </v-container>
</template>

<script>
import router from '@/router'
import store from '@/./store'

import { required, minLength } from 'vuelidate/lib/validators'

import ConfirmDialog from '@/components/common/ConfirmDialog'
import PageTitle from '@/components/common/PageTitle'

import { errorHandler } from '@/utils/errorHandler'

import dateFormatter from '@/mixins/dateFormatter'
import customFilters from '@/mixins/customFilters'
import parse from '@/mixins/parse'
import pagination from '@/mixins/dataTablePagination'
import tooltipDelay from '@/mixins/tooltip'
import { appConfig } from '@/config'
import { EventBus } from '@/utils/eventBus'
import { hasUserPermission } from '@/utils/permissionsHandler'

export default {
    name: 'NotesProductionSiteNotesProductionSiteNote',
    components: {
        PageTitle, ConfirmDialog
    },
    mixins: [ dateFormatter, customFilters, parse, pagination, tooltipDelay ],
    beforeRouteEnter(to, from, next) {
        // Edycja notki
        if (typeof to.params.id !== 'undefined') {
            Promise.all([
                store.dispatch('notes/getProductionSiteNote', to.params.id),
                store.dispatch('configurations/getProductionSites', {query: '?type_id=2,3,4,5'}),
            ])
                .then(responses => {
                    next(vm => {
                        vm.actionName = 'updateProductionSiteNote'
                        vm.productionSiteNote = Object.assign({}, responses[0])
                        vm.expireDateTime = vm.productionSiteNote.expireDateTime ? new Date(vm.productionSiteNote.expireDateTime): null
                        vm.pageTitle = to.meta.title
                    })
                })
                .catch(error => {
                    next(error)
                    errorHandler(error)
                })
        // Tworzenie notki
        } else {
            Promise.all([
                store.dispatch('configurations/getProductionSites', {query: '?type_id=2,3,4,5'}),
            ])
                .then(() => {
                    next(vm => {
                        vm.actionName = 'createProductionSiteNote'
                        vm.pageTitle = to.meta.title
                        vm.canAccept && (vm.productionSiteNote.adminAccepted = true)
                    })
                })
                .catch(error => {
                    next(error)
                    errorHandler(error)
                })
        }
    },
    data() {
        return {
            pageTitle: null,
            productionSiteNote: {
                title: null,
                text: null,
                expireDateTime: null,
                productionSites: [],
                productionSitesSerializer: [],
                approved: false,
                read: false,
                canComment: false,
                originProductionSiteId: null,
                productionSiteNoteReadByHistory: [],
                productionSiteNoteComments: [],
                createdBy: null
            },
            confirmDeleteNoteDialog: false,
            actionLoading: {
                saveNote: false,
                deleteNote: false,
                getNote: false,
                addComment: false
            },
            expireDateTime: null,
            actionName: null,
            datetimeProps: {
                textFieldProps: {
                    outlined: true,
                    dense: true,
                    appendIcon: 'mdi-calendar',
                    errorMessages: []
                },
                dateProps: {
                    firstDayOfWeek: 1
                },
                timeProps: {
                    format: '24hr',
                    scrollable: true
                }
            },
            readByHeaders: [
                {
                    text: 'Pracownik',
                    sortable: false,
                    value: 'readBy',

                },
                {
                    text: 'Data odczytu',
                    sortable: false,
                    value: 'readDateTime',
                }
            ],
            commentsHeaders: [
                {
                    text: 'Pracownik',
                    sortable: false,
                    value: 'createdBy',

                },
                {
                    text: 'Data utworzenia',
                    sortable: false,
                    value: 'createdAt',
                },
                {
                    text: 'Treść',
                    sortable: false,
                    value: 'comment'
                }
            ],
            noteComment: {
                productionSiteNoteId: null,
                comment: null,
            },
            search: null
        }
    },
    computed: {
        isNewNote() {
            return this.actionName === 'createProductionSiteNote'
        },
        canDelete() {
            return hasUserPermission('production_sites_notes_admin_delete')
        },
        canUpdate() {
            return hasUserPermission(['production_sites_notes_admin_update', 'production_sites_note_leader_update'])
        },
        canAccept() {
            return hasUserPermission('production_sites_notes_admin_update')
        },
        canUserCommentNote() {
            return this.productionSiteNote.canComment && this.productionSiteNote.approved // 1 komentarz/notkę + status opublikowana
        },
        titleErrors() {
            const errors = []

            if (!this.$v.productionSiteNote.title.$dirty) return errors
            !this.$v.productionSiteNote.title.required && errors.push('Proszę podać tytuł notki.')

            return errors
        },
        textErrors() {
            const errors = []

            if (!this.$v.productionSiteNote.text.$dirty) return errors
            !this.$v.productionSiteNote.text.required && errors.push('Proszę podać opis notki.')

            return errors
        },
        productionSitesErrors() {
            const errors = []

            if (!this.$v.productionSiteNote.productionSites.$dirty) return errors
            !this.$v.productionSiteNote.productionSites.required && errors.push('Proszę wybrać co najmniej jedno stanowisko.')

            return errors
        },
        allProductionSites() {
            return this.$store.state.configurations.productionSites.productionSites
        },
        footerPropsReadBy() {
            return {
                itemsPerPageOptions: appConfig.productionSiteNotes.rowsPerPageItems
            }
        },
        originProductionSite() {
            return this.productionSiteNote.originProductionSite ? this.productionSiteNote.originProductionSite.name : '-'
        },
        userData() {
            const user = this.productionSiteNote.createdBy
            
            if (user) {
                
                return `${user.firstName} ${user.lastName}`  
            }

            return '-'
        }
    },
    validations() {
        return {
            productionSiteNote: {
                title: {
                    required
                },
                text: {
                    required
                },
                productionSites: {
                    minLength: minLength(1),
                    required
                },
            },
            expireDateTime: {
                required
            },
        }
    },
    methods: {
        expireDateTimeHandler(dateTime) {
            this.validateExpireDateTime()
            this.expireDateTime = dateTime
            dateTime ? this.productionSiteNote.expireDateTime = dateTime.toISOString() : this.productionSiteNote.expireDateTime = null
        },
        validateExpireDateTime() {
            this.datetimeProps.textFieldProps.errorMessages = []
            if (this.$v.expireDateTime.$invalid) this.datetimeProps.textFieldProps.errorMessages.push('Proszę podać datę wygaśnięcia.')
        },
        close() {
            router.push({ name: 'notesProductionSiteNotes' })
                .catch(error => {
                    errorHandler(error)
                })
            this.$refs.expireDateTimePicker.clearHandler()
        },
        removeProductionSite(item) {
            this.productionSiteNote.productionSites.splice(this.productionSiteNote.productionSites.indexOf(item.id), 1)
        },
        deleteNote() {
            this.actionLoading.deleteNote = true
            this.$store.dispatch('notes/deleteProductionSiteNote', this.productionSiteNote.id)
                .then(() => {
                    this.close()
                    this.closeConfirmDeleteNoteDialog()
                })
                .catch(error => {
                    errorHandler(error)
                })
                .finally(() => {
                    this.actionLoading.deleteNote = false
                })
        },
        openConfirmDeleteNoteDialog(note) {
            this.confirmDeleteNoteDialog = true
            this.productionSiteNote = note
        },
        closeConfirmDeleteNoteDialog() {
            this.confirmDeleteNoteDialog = false
            this.productionSiteNote = {}
        },
        saveNote() {
            this.$v.$touch()

            if (this.$v.$invalid) {
                this.$store.dispatch('showNotification', {
                    type: 'error',
                    message: this.$i18n.t('validations.invalidMessage')
                })

                return
            }

            this.actionLoading.saveNote = true
            this.$store.dispatch(`notes/${this.actionName}`, this.productionSiteNote)
                .then(() => {
                    this.close()
                    if (this.isNewNote) {
                        let unread = this.$store.state.notes.productionSiteNotes.unreadProductionSiteNotes

                        EventBus.$emit('updatedProductionSiteNoteCount', unread + 1)
                    }
                })
                .catch(error => {
                    errorHandler(error)
                })
                .finally(() => {
                    this.actionLoading.saveNote = false
                })
        },
        addComment() {
            this.actionLoading.addComment = true
            this.noteComment.productionSiteNoteId = this.productionSiteNote.id
            this.$store.dispatch('notes/createProductionSiteNoteComment', this.noteComment)
                .then((response) => {
                    this.noteComment = {productionSiteNoteId: null, comment: null}
                    this.productionSiteNote.productionSiteNoteComments.push(response.data)
                    this.productionSiteNote.canComment = false
                })
                .catch(error => {
                    errorHandler(error)
                })
                .finally(() => {
                    this.actionLoading.addComment = false
                })
        },
        changeNoteAcceptance() {
            this.approved = !this.approved
        },
        approveLabel() {
            return this.productionSiteNote.approved === true ? 'publiczna' : 'niepubliczna'
        },
        acceptLabel() {
            return this.productionSiteNote.adminAccepted === true ? 'zatwierdzona' : 'nie zatwierdzona'
        },
    }
}
</script>